import type { Notebook } from "@engram/core";
import { useQuery } from "../db/client";
import { collator } from "./format";

/*
 * Stacks and notebooks as a tree. The data model always allowed any depth (parent_id); the UI shows
 * whatever the data holds, and only lets you put a stack inside another stack when the synced setting
 * `notebooks.nesting` is on (Customize sidebar). Evernote itself stops at two levels.
 */

/** SQL: the ids of a stack and everything under it, at any depth (one bound parameter: the stack id). */
export const SUBTREE_SQL = "(WITH RECURSIVE sub(id) AS (SELECT ? UNION SELECT nb.id FROM notebooks nb JOIN sub ON nb.parent_id = sub.id WHERE nb.deleted = 0) SELECT id FROM sub)";

type Node = Pick<Notebook, "id" | "parent_id" | "kind" | "name">;

export interface Tree<T extends Node> {
  /** Top level: rows without a (live) parent stack, stacks and notebooks mixed, by name. */
  top: T[];
  children: (stackId: string) => T[];
  /** Every notebook under a stack, at any depth. */
  notebooksUnder: (stackId: string) => T[];
  /** Is `id` the stack `ancestor` or inside it? (Guards against moving a stack into itself.) */
  within: (id: string, ancestor: string) => boolean;
}

export function buildTree<T extends Node>(rows: T[]): Tree<T> {
  const stacks = new Map(rows.filter((r) => r.kind === "stack").map((r) => [r.id, r]));
  const kids = new Map<string, T[]>();
  const top: T[] = [];
  for (const r of rows) {
    const parent = r.parent_id && r.parent_id !== r.id ? stacks.get(r.parent_id) : undefined;
    if (parent) kids.set(parent.id, [...(kids.get(parent.id) ?? []), r]);
    else top.push(r);
  }
  const byName = (a: T, b: T) => collator.compare(a.name, b.name);
  top.sort(byName);
  for (const list of kids.values()) list.sort(byName);
  const children = (id: string) => kids.get(id) ?? [];
  const notebooksUnder = (id: string, seen = new Set<string>()): T[] => {
    if (seen.has(id)) return [];
    seen.add(id);
    return children(id).flatMap((c) => (c.kind === "stack" ? notebooksUnder(c.id, seen) : [c]));
  };
  const within = (id: string, ancestor: string) => {
    let cur: string | null | undefined = id;
    for (let i = 0; cur && i < 64; i++) {
      if (cur === ancestor) return true;
      cur = stacks.get(cur)?.parent_id ?? rows.find((r) => r.id === cur)?.parent_id;
    }
    return false;
  };
  return { top, children, notebooksUnder: (id) => notebooksUnder(id), within };
}

export function useNesting(): boolean {
  const row = useQuery<{ value: string }>("SELECT value FROM settings WHERE id = 'notebooks.nesting' AND deleted = 0", [], ["settings"])?.[0];
  return row ? JSON.parse(row.value) === true : false;
}
